<template>
  <BaseDropdown v-if="!pending && !$authStore.isCurrentUserAnonymous" tag="li" :menu-on-right="true" title-tag="a" class="nav-item" title-classes="nav-link"
                menu-classes="dropdown-navbar">
    <template #title>
      <div class="photo">
        <img :src="avatarInfo?.avatar" :alt="avatarInfo!.avatarAltText" />
      </div>
      <b class="caret"></b>
    </template>
    <li class="nav-link" v-if="withProfileLink && !technicalUser && !checkRouteIfOnRegistrationPage">
      <a class="nav-item dropdown-item" @click="$router.push('/dashboard/meine-datenbanken')">Meine Datenbanken</a>
    </li>
    <li class="nav-link" v-if="configDatabaseId && isDbOwner">
      <a class="nav-item dropdown-item" @click="$router.push(`/dashboard/meine-datenbanken/${configDatabaseId}`)">Konfiguration</a>
    </li>
    <!--
      ══ „LESEZEICHEN" — DIE KACHELSEITE DER DATENBANK ═══════════════════════

      Sie steht HIER und nicht in den sechs Leser-Kopfzeilen, die dieses Menü
      einbinden: einmal gepflegt gilt sie für jeden Aufbau.

      Für ANONYME Besucher erscheint sie nicht — das Menü selbst erscheint für
      sie nicht (`isCurrentUserAnonymous` oben am Auswahlfeld). So wollte es
      der Betreiber am 28.09.2026. Sie erreichen die Seite über den Link
      `db:slides:gallery`.
    -->
    <li class="nav-link" v-if="zeigeLesezeichen">
      <a class="nav-item dropdown-item" @click="oeffneLesezeichen">Lesezeichen</a>
    </li>
    <slot name="additional"></slot>

    <div class="dropdown-divider" v-if="superAdminDB"></div>
    <!--
      ══ AUCH DIESE BEIDEN TRAGEN `nav-link` (Betreiber 03.09.2026) ═══════════

      „Zwei Menüpunkte sind ausgerückt. Nur bei der Ansicht der Datenbank."

      Genau so gemessen: In der Leseransicht standen „Benutzerverwaltung" und
      „Statistik" bei x=1303, alle übrigen bei x=1309 — sechs Pixel weiter
      links. Der Grund war die fehlende Klasse; sie bringt in diesem Menü den
      seitlichen Abstand mit. Im Dashboard fiel es nicht auf, weil dort ein
      anderer Stil greift — deshalb „nur bei der Datenbank".
    -->
    <li class="nav-link" v-if="superAdminDB">
      <a class="nav-item dropdown-item" href="https://id.datenbankgestalter.de/admin/master/console/" target="_blank">Benutzerverwaltung</a>
    </li>
    <li class="nav-link" v-if="superAdminDB">
      <a class="nav-item dropdown-item" href="https://analytics.datenbankgestalter.de" target="_blank">Statistik</a>
    </li>

    <div class="dropdown-divider" v-if="(withProfileLink || withHelpLink) && !checkRouteIfOnRegistrationPage"></div>
    <li class="nav-link">
      <a class="nav-item dropdown-item" @click="doSignOut">Abmelden</a>
    </li>
  </BaseDropdown>
</template>

<script lang="ts" setup>
  import { ApplicationScope, TenancyRole } from '~/api-client';
  import { galerieAktiv } from '~/components/MyDb/Core/Views/composables/linkGallery';
  import { useMyDbStore } from '~/store/mydb';
  import { buildImageUrlFromBlob } from '~/modules/common/services/util/util';
  import { storeToRefs } from 'pinia';

  const props = defineProps({
    withProfileLink: {
      type: Boolean,
      default: true,
    },
    withHelpLink: {
      type: Boolean,
      default: false,
    },
    configDatabaseId: {
      type: String,
      default: null,
    },
    configDatabaseOwnerId: {
      type: String,
      default: null,
    },
  });

  const { signOut } = useAuth();
  const pathAlias = useRoute();
  const $mydb = useMyDbStore();

  /* Nur in der Leseransicht einer Datenbank, die eine Kachelseite hat. Die
     Route grenzt das Dashboard aus: dort ist dieselbe Datenbank womöglich
     geladen, aber „Lesezeichen" gehört dem Besucher, nicht der Konfiguration. */
  const zeigeLesezeichen = computed(() =>
    pathAlias.path.startsWith('/datenbank/') && galerieAktiv($mydb.currentDatabase));

  /*
   * Oeffnen heisst: die Adresse ergaenzen, nicht einen Merker setzen. Die
   * Kachelseite hat kein Schliessen-Zeichen mehr (Betreiber 28.09.2026) — der
   * Rueckweg ist der Zurueck-Knopf des Browsers, und den gibt es nur, wenn
   * sich die Adresse aendert.
   */
  const oeffneLesezeichen = () =>
    navigateTo({ query: { ...pathAlias.query, slides: 'gallery' } });

  const checkRouteIfOnRegistrationPage = computed(() => {
    return pathAlias.path === '/registration';
  });

  const { $backendService, $logger, $router, $customerStore, $authStore, $tenant } = useNuxtApp();

  const doSignOut = async () => {
    await signOut({ callbackUrl: '/' });
  };

  const { currentCustomer: currentCustomerInfo } = storeToRefs($customerStore);

  const superAdminDB = computed(() => {
    return !!currentCustomerInfo.value.tenancyRoles?.find((t: TenancyRole) => t.roles?.includes('SUPER_ADMIN') && t.tenant === ApplicationScope.MyDb);
  });

  const isDbOwner = computed(() => {
    if (!props.configDatabaseOwnerId) return false;
    return currentCustomerInfo.value?.id === props.configDatabaseOwnerId;
  });

  const technicalUser = computed(() => {
    return !!currentCustomerInfo.value.tenancyRoles?.find((t: TenancyRole) => t.roles?.includes('TECHNICAL_USER') && t.tenant === $tenant.getTenant);
  });

  const { data: avatarData, pending } = await useAsyncData(async () => {
    let userInfo;
    try {
      userInfo = await $backendService.customerControllerApi.getCustomer({ xApplicationScope: ApplicationScope.MyDb });
    } catch (e) {
      console.log('unable to get user info, although user is authenticated?', e);
    }

    let userPicture;
    try {
      userPicture = await $backendService.customerControllerApi.getCustomerImage({ xApplicationScope: ApplicationScope.MyDb });
    } catch (error) {
      $logger.error('unable to load profile image', error);
    }

    let username = '';
    if (!!userInfo?.firstName && userInfo.firstName.length > 1) {
      username += userInfo.firstName.substring(0, 1);
      if (!!userInfo.lastName && userInfo.lastName.length > 1) {
        username += ' ' + userInfo.lastName.substring(0, 1);
      }
    } else if (!!userInfo?.lastName && userInfo.lastName.length > 1) {
      username += userInfo.lastName.substring(0, 1);
    } else {
      username = 'John Doe';
    }

    let avatarUrl = await buildImageUrlFromBlob(userPicture);
    if (!avatarUrl) {
      avatarUrl = 'https://eu.ui-avatars.com/api/?background=ff8d72&color=fff&size=256&name=' + username;
    }

    return {
      avatar: avatarUrl,
      username: username,
      avatarAltText: username,
    };
  });

  const avatarInfo = unref(avatarData);
  onMounted(async () => {
    await $customerStore.getCurrentCustomer();
  });
</script>
<style scoped>
:deep(a.nav-link.dropdown-toggle) {
  background: transparent !important;
}

/* Listenpunkt entfernen — das <li> wird teils ohne <ul>-Wrapper gerendert
   (z.B. mobile Header in layouts/main.vue), Browser zeigt sonst Bullet.
   Kein :deep() weil das den Root nicht erwischt. Direkter Selector matcht
   den BaseDropdown-Root via Vue3 scoped-CSS-Hash-Vererbung. */
.nav-item {
  list-style: none !important;
}

/* Default-Bootstrap-Caret (::after auf .dropdown-toggle) ausblenden,
   damit nur unser eigener .caret sichtbar ist. */
:deep(.dropdown-toggle::after) {
  display: none !important;
}

/* Eigenes Caret-Dreieck weiß und korrekt positioniert. */
:deep(.caret) {
  position: static !important;
  margin-left: 6px !important;
  margin-top: 0 !important;
  border-top: 4px solid white !important;
  border-right: 4px solid transparent !important;
  border-left: 4px solid transparent !important;
  vertical-align: middle !important;
  display: inline-block !important;
}

/* Avatar-Bild rund 30x30 (überall, wo AccountIcon eingehängt ist). */
:deep(.photo) {
  display: inline-block;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  vertical-align: middle;
  overflow: hidden;
}
:deep(.photo img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ───────────────────────────────────────────────────────────────────────
   Avatar-/Benutzermenü: feste Markenfarbe #3a7cd4 + Weiß — GILT FÜR ALLE
   Einbindungen (Reader-Header UND Owner-Floating-Navbar, Dashboard, …), da
   beide dieses AccountIcon nutzen. Vorher lag der Stil nur in
   dashboard/_navbar.scss und griff bei der Desktop-Floating-Navbar NICHT →
   weiß auf weiß (unsichtbar); mobil (Header-Avatar) war bereits korrekt.
   Hier zentral, mit !important + Component-Chunk-Ladereihenfolge. */
:deep(.dropdown-menu.dropdown-navbar) {
  background-color: #3a7cd4 !important;
  border: none !important;
  border-radius: 8px !important;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25) !important;
  padding: 6px 0 !important;
  min-width: 210px !important;
  z-index: 2000 !important;
}

/* Avatar-Menü klickbar ERZWINGEN. Die feste .navbar-absolute-Leiste im
   Reader hat pointer-events:none (klick-durchlaessiger Streifen ueber dem
   Header-Grid). Das darf NICHT auf das Menue + seine Eintraege durchschlagen —
   sonst gehen Klicks durch das (sichtbar oben liegende) Menue hindurch auf den
   dahinterliegenden Body-Text, und „Konfiguration"/„Listenansicht" reagieren
   nicht (User-Befund 2026-08, per Inspect bestaetigt: Rechtsklick markierte das
   <p> im Body statt des Menuepunkts). Der bestehende selektive Re-Enable in
   _navbar.scss greift hier nicht zuverlaessig — daher hart am Menue erzwingen. */
:deep(.dropdown-menu.dropdown-navbar),
:deep(.dropdown-menu.dropdown-navbar) * {
  pointer-events: auto !important;
}
:deep(.dropdown-menu.dropdown-navbar) li,
:deep(.dropdown-menu.dropdown-navbar) li a,
:deep(.dropdown-menu.dropdown-navbar) .nav-link,
:deep(.dropdown-menu.dropdown-navbar) .dropdown-item,
:deep(.dropdown-menu.dropdown-navbar) a.dropdown-item {
  color: #fff !important;
  background-color: transparent !important;
}
:deep(.dropdown-menu.dropdown-navbar) .dropdown-item:hover,
:deep(.dropdown-menu.dropdown-navbar) .dropdown-item:focus,
:deep(.dropdown-menu.dropdown-navbar) li a:hover,
:deep(.dropdown-menu.dropdown-navbar) li a:focus {
  background-color: rgba(255, 255, 255, 0.16) !important;
}
:deep(.dropdown-menu.dropdown-navbar) .dropdown-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.45) !important;
  opacity: 1 !important;
}
</style>
